import { useTranslation } from 'react-i18next';
import PageHeader from '@casops/components/layout/page-header/PageHeader';
import { ModelName, QueryTag, TagList } from '@casops/store/api';
import StandardPage from '@casops/templates/standard-page/StandardPage';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import { Permission } from '@casops/permissions';
import { LibraryItemModel, useGetListLibraryQuery } from '@casops/store/apis/libraryApi';
import useLibraryColDefs from './useLibraryColDefs';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { SidePanel } from '@casops/store/slices/globalSlice';

type Model = LibraryItemModel;

const modelName = ModelName.LibraryItem;
const sidePanel = SidePanel.LibraryUpload;
const permission = Permission.Admin;

const tagsToClearOnRefresh = [
  { type: QueryTag.LibraryItem, id: TagList },
  { type: QueryTag.SettingsLibraryItemType, id: TagList },
];

export function LibraryPage() {
  const { t } = useTranslation();

  const columnDefs = useLibraryColDefs({ modelName, permission });

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useGetListLibraryQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  return (
    <StandardPage
      header={
        <PageHeader title={t('Navigation.Library')} tagsToClearOnRefresh={tagsToClearOnRefresh} />
      }
    >
      <CollectionView<Model>
        sliceKey={modelName}
        modelName={modelName}
        columnDefs={columnDefs}
        rowData={data}
        hasData={hasData}
        noDataDescription={noDataDescription}
        actions={
          <CreateItemButton
            modelName={modelName}
            sidePanel={sidePanel}
            permission={permission}
            label={t('Upload')}
            icon="upload"
          />
        }
      />
    </StandardPage>
  );
}

export default LibraryPage;
